/**
 * AdminExecutive — Ecosystem-wide executive intelligence dashboard.
 *
 * Route: /admin/executive (super-admin only)
 * Data: single call to GET /api/admin/executive-summary
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  BarChart3, Users, Calendar, GraduationCap, Building2, Heart,
  FileText, Bell, RefreshCw, Wallet, Layers, TrendingUp, GitCompare,
} from 'lucide-react';
import {
  LineChart, Line, ResponsiveContainer, Tooltip, YAxis, XAxis, Legend, CartesianGrid,
} from 'recharts';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

// Sparkline — tiny 30-day trend chart used inside tiles.
const Sparkline = ({ series, buckets, tone = 'violet', testid, height = 40, isMoney = false }) => {
  const data = (series || []).map((v, i) => ({ i, v: Number(v || 0), day: (buckets || [])[i] }));
  const hasData = data.some((d) => d.v > 0);
  const strokes = {
    violet:  '#a78bfa',
    emerald: '#34d399',
    amber:   '#fbbf24',
    rose:    '#fb7185',
    sky:     '#38bdf8',
    slate:   '#cbd5e1',
  };
  const stroke = strokes[tone] || strokes.violet;
  if (!hasData) {
    return (
      <div className="h-10 text-[10px] text-slate-500 opacity-60 flex items-center" data-testid={testid}>
        no activity
      </div>
    );
  }
  return (
    <div data-testid={testid} className="h-10 -mx-1">
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data}>
          <YAxis hide domain={['auto', 'auto']} />
          <Tooltip
            cursor={false}
            wrapperStyle={{ outline: 'none' }}
            contentStyle={{
              background: 'rgba(15,23,42,0.95)',
              border: '1px solid rgba(148,163,184,0.3)',
              borderRadius: 6,
              fontSize: 11,
              padding: '4px 8px',
            }}
            labelStyle={{ color: '#94a3b8', fontSize: 10 }}
            formatter={(v) => [isMoney ? Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 }) : v, isMoney ? 'Amount' : 'Count']}
            labelFormatter={(_, p) => p?.[0]?.payload?.day || ''}
          />
          <Line
            type="monotone"
            dataKey="v"
            stroke={stroke}
            strokeWidth={1.8}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// Compare Workspaces — overlay every workspace's revenue in one chart.
// Users toggle each workspace on/off; the shared axis makes it obvious
// which workspace pulled ahead this period.
const WS_COLORS = {
  events:    '#a78bfa',  // violet
  school:    '#34d399',  // emerald
  church:    '#fb7185',  // rose
  donations: '#c084fc',  // purple
  bookshop:  '#38bdf8',  // sky
  pos:       '#cbd5e1',  // slate
  housing:   '#fbbf24',  // amber
  studio:    '#f472b6',  // pink
  campaigns: '#f97316',  // orange
};

const CompareWorkspacesCard = ({ revByWs, buckets, days }) => {
  const workspaces = Object.keys(revByWs || {}).filter(
    (k) => (revByWs[k] || []).some((v) => Number(v) > 0),
  ).sort();
  const [expanded, setExpanded] = useState(false);
  const [cumulative, setCumulative] = useState(false);
  const [enabled, setEnabled] = useState(() =>
    workspaces.reduce((acc, k) => { acc[k] = true; return acc; }, {}),
  );

  // Keep enabled state in sync as workspaces list changes
  React.useEffect(() => {
    setEnabled((prev) => {
      const next = { ...prev };
      workspaces.forEach((k) => { if (!(k in next)) next[k] = true; });
      Object.keys(next).forEach((k) => { if (!workspaces.includes(k)) delete next[k]; });
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaces.join(',')]);

  // Build recharts data rows: [{day, events: 100, school: 200, ...}]
  // When cumulative=true each cell holds the running total up to that day.
  const rows = (buckets || []).map((day) => ({ day }));
  workspaces.forEach((ws) => {
    let running = 0;
    (buckets || []).forEach((_day, i) => {
      const daily = Number((revByWs[ws] || [])[i] || 0);
      running += daily;
      rows[i][ws] = cumulative ? running : daily;
    });
  });

  if (!expanded) {
    return (
      <button
        data-testid="compare-workspaces-toggle"
        onClick={() => setExpanded(true)}
        className="flex w-full items-center justify-between rounded-xl border border-slate-800 bg-slate-900/60 px-5 py-3 text-left transition-colors hover:border-violet-500/40 hover:bg-slate-900"
      >
        <div className="flex items-center gap-3">
          <GitCompare className="h-5 w-5 text-violet-300" />
          <div>
            <div className="text-sm font-semibold text-slate-200">Compare Workspaces</div>
            <div className="text-xs text-slate-500">
              Overlay every workspace's {days}-day revenue in one chart
            </div>
          </div>
        </div>
        <span className="text-xs text-slate-500">click to expand</span>
      </button>
    );
  }

  return (
    <Card className="border-violet-500/30 bg-slate-900/60" data-testid="compare-workspaces-card">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-base">
          <GitCompare className="h-4 w-4 text-violet-300" />
          Compare Workspaces · Revenue ({days}d)
          {cumulative && (
            <Badge variant="outline" className="ml-2 border-emerald-500/40 text-emerald-300">
              cumulative
            </Badge>
          )}
        </CardTitle>
        <div className="flex items-center gap-2">
          <Button
            data-testid="compare-cumulative-toggle"
            variant="outline"
            size="sm"
            onClick={() => setCumulative((v) => !v)}
            className={
              cumulative
                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20'
                : 'border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800'
            }
          >
            {cumulative ? 'Daily' : 'Cumulative'}
          </Button>
          <Button
            data-testid="compare-collapse"
            variant="ghost"
            size="sm"
            className="text-slate-400 hover:text-slate-100"
            onClick={() => setExpanded(false)}
          >
            Collapse
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Legend toggles */}
        <div className="mb-3 flex flex-wrap gap-2" data-testid="compare-legend">
          {workspaces.length === 0 && (
            <p className="text-xs text-slate-500">No revenue data across workspaces in this window.</p>
          )}
          {workspaces.map((ws) => {
            const on = enabled[ws] !== false;
            const total = (revByWs[ws] || []).reduce((a, b) => a + Number(b || 0), 0);
            return (
              <button
                type="button"
                key={ws}
                data-testid={`compare-toggle-${ws}`}
                onClick={() => setEnabled((e) => ({ ...e, [ws]: !on }))}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                  on
                    ? 'border-slate-600 text-slate-100'
                    : 'border-slate-800 text-slate-500 line-through opacity-60'
                }`}
                style={on ? { boxShadow: `inset 0 0 0 1px ${WS_COLORS[ws] || '#a78bfa'}` } : {}}
              >
                <span
                  className="mr-1.5 inline-block h-2 w-2 rounded-full align-middle"
                  style={{ background: WS_COLORS[ws] || '#a78bfa' }}
                />
                {ws} · {total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </button>
            );
          })}
        </div>

        <div className="h-64" data-testid="compare-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#64748b' }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10, fill: '#64748b' }} />
              <Tooltip
                contentStyle={{
                  background: 'rgba(15,23,42,0.95)',
                  border: '1px solid rgba(148,163,184,0.3)',
                  borderRadius: 6,
                  fontSize: 12,
                }}
                labelStyle={{ color: '#94a3b8', fontSize: 11 }}
                formatter={(v, name) => [Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 }), name]}
              />
              {workspaces.filter((ws) => enabled[ws] !== false).map((ws) => (
                <Line
                  key={ws}
                  type="monotone"
                  dataKey={ws}
                  stroke={WS_COLORS[ws] || '#a78bfa'}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

const Tile = ({ icon: Icon, label, value, sub, tone = 'slate', testid, onClick, trend, buckets, isMoney }) => {  const tones = {
    slate: 'from-slate-500/15 to-slate-500/5 text-slate-300 border-slate-400/30',
    violet: 'from-violet-500/15 to-violet-500/5 text-violet-300 border-violet-400/30',
    emerald: 'from-emerald-500/15 to-emerald-500/5 text-emerald-300 border-emerald-400/30',
    amber: 'from-amber-500/15 to-amber-500/5 text-amber-300 border-amber-400/30',
    rose: 'from-rose-500/15 to-rose-500/5 text-rose-300 border-rose-400/30',
    sky: 'from-sky-500/15 to-sky-500/5 text-sky-300 border-sky-400/30',
  };
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`rounded-xl border bg-gradient-to-br p-5 text-left transition-all ${tones[tone]} ${
        onClick ? 'hover:shadow-lg hover:border-opacity-60 cursor-pointer' : ''
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wider opacity-80">{label}</span>
        <Icon className="h-5 w-5 opacity-70" />
      </div>
      <div className="mt-3 text-3xl font-semibold">{value ?? '—'}</div>
      {sub && <div className="mt-1 text-xs opacity-70">{sub}</div>}
      {trend && (
        <div className="mt-2">
          <Sparkline
            series={trend}
            buckets={buckets}
            tone={tone}
            testid={testid ? `${testid}-sparkline` : undefined}
            isMoney={isMoney}
          />
        </div>
      )}
    </button>
  );
};

const fmtMoney = (byCur) => {
  if (!byCur || !Object.keys(byCur).length) return '—';
  return Object.entries(byCur)
    .map(([c, a]) => `${c} ${Number(a).toLocaleString(undefined, { maximumFractionDigits: 0 })}`)
    .join('  ·  ');
};

export default function AdminExecutive() {
  const navigate = useNavigate();
  const [days, setDays] = React.useState(30);

  const q = useQuery({
    queryKey: ['exec-summary', days],
    queryFn: async () => {
      const r = await axios.get(`${API}/api/admin/executive-summary?days=${days}`, { headers: authH() });
      return r.data;
    },
    refetchInterval: 60_000,
  });

  const trendsQ = useQuery({
    queryKey: ['exec-trends', days],
    queryFn: async () => {
      const r = await axios.get(`${API}/api/admin/executive-summary/trends?days=${days}`, { headers: authH() });
      return r.data;
    },
    refetchInterval: 60_000,
  });

  const data = q.data || {};
  const trends = trendsQ.data || {};
  const buckets = trends.buckets || [];
  const revByWs = (trends.series && trends.series.revenue && trends.series.revenue.by_workspace) || {};
  const revTotal = (trends.series && trends.series.revenue && trends.series.revenue.total) || [];
  const registrationsSeries = (trends.series && trends.series.registrations) || [];
  const documentsSeries = (trends.series && trends.series.documents) || [];
  const notificationsSeries = (trends.series && trends.series.notifications) || [];
  const people = data.people || {};
  const revenue = data.revenue || {};
  const events = data.events || {};
  const school = data.school || {};
  const hospital = data.hospital || {};
  const campaigns = data.campaigns || {};
  const documents = data.documents || {};
  const notifications = data.notifications || {};

  return (
    <div className="min-h-screen bg-slate-950 p-6 text-slate-100" data-testid="admin-executive-page">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="flex items-center gap-3 text-3xl font-bold">
              <BarChart3 className="h-7 w-7 text-violet-400" />
              Executive Intelligence
            </h1>
            <p className="mt-1 text-sm text-slate-400">
              Ecosystem-wide snapshot · aggregated from shared platform services
              (Identity Registry · Unified Ledger · Document Audit · Notification Log).
            </p>
          </div>
          <div className="flex items-center gap-2">
            {[7, 30, 90, 365].map((n) => (
              <Button
                key={n}
                data-testid={`window-${n}d`}
                variant={days === n ? 'default' : 'outline'}
                size="sm"
                className={days === n ? 'bg-violet-600 hover:bg-violet-500' : 'border-slate-700 bg-slate-900 hover:bg-slate-800'}
                onClick={() => setDays(n)}
              >
                {n}d
              </Button>
            ))}
            <Button
              data-testid="exec-refresh-btn"
              variant="outline"
              size="sm"
              className="border-slate-700 bg-slate-900 hover:bg-slate-800"
              onClick={() => q.refetch()}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Revenue Hero — hidden entirely if the caller lacks `finance.view.totals` */}
        {revenue?.restricted ? (
          <Card className="border-amber-500/30 bg-slate-900/60" data-testid="revenue-restricted-card">
            <CardContent className="p-4 flex items-center gap-3 text-sm">
              <Wallet className="h-5 w-5 text-amber-300 flex-shrink-0" />
              <div>
                <p className="font-semibold text-amber-200">Finance totals are restricted</p>
                <p className="text-xs text-slate-500">
                  Revenue is hidden for your account. Ask a super-admin to grant{' '}
                  <code className="text-amber-300">finance.view.totals</code> via Team Permissions.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
        <Card className="border-violet-500/30 bg-gradient-to-br from-violet-500/10 to-slate-950">
          <CardContent className="p-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm font-semibold text-violet-300">Total Ecosystem Revenue</p>
                <p className="mt-1 text-xs text-slate-500">Aggregated across every workspace</p>
                <div className="mt-3 text-4xl font-bold" data-testid="exec-revenue-total">
                  {fmtMoney(revenue.total_income_by_currency)}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(revenue.by_workspace || []).map((w) => {
                    const total = Object.values(w.revenue_by_currency || {}).reduce((a, b) => a + Number(b || 0), 0);
                    return (
                      <Badge
                        key={w.workspace}
                        data-testid={`workspace-chip-${w.workspace}`}
                        variant="outline"
                        className="border-violet-500/40 text-violet-300"
                      >
                        {w.workspace} · {total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </Badge>
                    );
                  })}
                </div>
                <p className="mt-3 text-xs text-slate-500">
                  {revenue.transaction_count ?? 0} transactions · last {data.window_days ?? days} days
                </p>
                <div className="mt-4 h-16" data-testid="revenue-hero-sparkline-wrap">
                  <Sparkline series={revTotal} buckets={buckets} tone="violet" isMoney height={60} testid="revenue-hero-sparkline" />
                </div>
              </div>
              <div className="h-16 w-16 flex-shrink-0 rounded-full bg-violet-500/15 flex items-center justify-center">
                <Wallet className="h-8 w-8 text-violet-300" />
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        {/* People */}
        <div>
          <h2 className="mb-3 text-xs uppercase tracking-wider text-slate-400">People</h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <Tile testid="people-persons" icon={Users} label="Persons" value={people.persons} tone="violet" onClick={() => navigate('/admin/persons')} />
            <Tile testid="people-delegates" icon={Users} label="Delegates" value={people.delegates} tone="sky" />
            <Tile testid="people-students" icon={GraduationCap} label="Students" value={people.students} tone="emerald" />
            <Tile testid="people-members" icon={Users} label="Church members" value={people.church_members} tone="amber" />
            <Tile testid="people-admins" icon={Users} label="Admins" value={people.admin_users} tone="slate" />
          </div>
        </div>

        {/* Compare Workspaces overlay chart */}
        <CompareWorkspacesCard
          revByWs={revByWs}
          buckets={buckets}
          days={data.window_days ?? days}
        />

        {/* Workspaces */}
        <div>
          <h2 className="mb-3 text-xs uppercase tracking-wider text-slate-400">Workspaces</h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Tile testid="ws-events" icon={Calendar} label="Events · active" value={events.active}
              sub={`${events.registrations_window ?? 0} new registrations in ${data.window_days ?? days}d`}
              tone="violet"
              trend={revByWs.events || registrationsSeries} buckets={buckets} isMoney={!!revByWs.events}
              onClick={() => navigate(`/admin/finance/ledger?workspace=events&days=${days}`)} />
            <Tile testid="ws-school" icon={GraduationCap} label="School · certificates issued" value={school.certificates_issued}
              sub={`${school.upcoming_exams ?? 0} upcoming exams`}
              tone="emerald"
              trend={revByWs.school} buckets={buckets} isMoney
              onClick={() => navigate(`/admin/finance/ledger?workspace=school&days=${days}`)} />
            <Tile testid="ws-church" icon={Building2} label="Church · giving" value={
              (revByWs.church || []).reduce((a, b) => a + Number(b || 0), 0).toLocaleString(undefined, { maximumFractionDigits: 0 }) || '0'
            }
              sub={`Tithes + offerings · ${data.window_days ?? days}d`}
              tone="rose"
              trend={revByWs.church} buckets={buckets} isMoney
              onClick={() => navigate(`/admin/finance/ledger?workspace=church&days=${days}`)} />
            <Tile testid="ws-campaigns" icon={TrendingUp} label="Campaigns · active" value={campaigns.active}
              sub={`Pledged: ${Number(campaigns.total_pledged || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
              tone="amber"
              trend={revByWs.donations || revByWs.campaigns} buckets={buckets} isMoney
              onClick={() => navigate(`/admin/finance/ledger?workspace=donations&days=${days}`)} />
            <Tile testid="ws-bookshop" icon={FileText} label="Bookshop · sales" value={
              (revByWs.bookshop || []).reduce((a, b) => a + Number(b || 0), 0).toLocaleString(undefined, { maximumFractionDigits: 0 }) || '0'
            }
              sub={`Book orders · ${data.window_days ?? days}d`}
              tone="sky"
              trend={revByWs.bookshop} buckets={buckets} isMoney
              onClick={() => navigate(`/admin/finance/ledger?workspace=bookshop&days=${days}`)} />
            <Tile testid="ws-pos" icon={Wallet} label="POS · takings" value={
              (revByWs.pos || []).reduce((a, b) => a + Number(b || 0), 0).toLocaleString(undefined, { maximumFractionDigits: 0 }) || '0'
            }
              sub={`Point of sale · ${data.window_days ?? days}d`}
              tone="slate"
              trend={revByWs.pos} buckets={buckets} isMoney
              onClick={() => navigate(`/admin/finance/ledger?workspace=pos&days=${days}`)} />
            <Tile testid="ws-hospital" icon={Heart} label="Hospital · occupancy" value={`${hospital.occupancy_pct ?? 0}%`}
              sub={`${hospital.occupied ?? 0}/${hospital.beds ?? 0} beds`}
              tone="rose" />
            <Tile testid="ws-donations" icon={Heart} label="Donations" value={
              (revByWs.donations || []).reduce((a, b) => a + Number(b || 0), 0).toLocaleString(undefined, { maximumFractionDigits: 0 }) || '0'
            }
              sub={`Direct gifts · ${data.window_days ?? days}d`}
              tone="violet"
              trend={revByWs.donations} buckets={buckets} isMoney
              onClick={() => navigate(`/admin/finance/ledger?workspace=donations&days=${days}`)} />
          </div>
        </div>

        {/* Platform Activity */}
        <div>
          <h2 className="mb-3 text-xs uppercase tracking-wider text-slate-400">Platform activity ({data.window_days ?? days}d)</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Card className="border-slate-800 bg-slate-900/60">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <FileText className="h-4 w-4 text-emerald-400" /> Documents produced
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold" data-testid="documents-window">{documents.generated_window ?? 0}</div>
                <div className="mt-2">
                  <Sparkline series={documentsSeries} buckets={buckets} tone="emerald" testid="documents-sparkline" />
                </div>
                <div className="mt-3 space-y-1 text-sm text-slate-400">
                  {(documents.top_types || []).map((t) => (
                    <div key={t.type} className="flex items-center justify-between" data-testid={`doc-type-${t.type}`}>
                      <span>{t.type}</span>
                      <span className="font-medium text-slate-300">{t.count}</span>
                    </div>
                  ))}
                  {(!documents.top_types || documents.top_types.length === 0) && (
                    <div className="text-xs text-slate-500">No documents in this window.</div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-800 bg-slate-900/60">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Bell className="h-4 w-4 text-sky-400" /> Notifications sent
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold" data-testid="notifications-window">{notifications.sent_window ?? 0}</div>
                <div className="mt-2">
                  <Sparkline series={notificationsSeries} buckets={buckets} tone="sky" testid="notifications-sparkline" />
                </div>
                <div className="mt-3 space-y-1 text-sm text-slate-400">
                  {(notifications.by_channel || []).map((r, i) => (
                    <div key={i} className="flex items-center justify-between" data-testid={`notif-${r.channel}-${r.status}`}>
                      <span>{r.channel} · <span className="text-slate-500">{r.status}</span></span>
                      <span className="font-medium text-slate-300">{r.count}</span>
                    </div>
                  ))}
                  {(!notifications.by_channel || notifications.by_channel.length === 0) && (
                    <div className="text-xs text-slate-500">No notifications in this window.</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500">
          Generated {data.generated_at ? new Date(data.generated_at).toLocaleString() : '—'}
          <span className="mx-2 opacity-40">·</span>
          <Layers className="mr-1 inline h-3 w-3" /> Reads only shared platform services
        </p>
      </div>
    </div>
  );
}
